<template>
  <Stack class="stk-p-8 stk-gap-6">
    <Box class="cases-list">
      <BoxHead>
        <Title>Case</Title>
      </BoxHead>
      <div class="cases-toolbar">
        <router-link :to="{ name: 'cases-home' }">Back to Cases</router-link>
      </div>
      <div v-if="loadError" class="form-error">
        {{ loadError }}
      </div>
      <div v-else>
        <div v-if="!isCaseLinked" class="case-link-panel">
          <Title class="stk-mb-2" size="h5">Link Case</Title>
          <EntitySearchWithActions
            label="Find entity"
            :current="selectedEntityOption"
            :disabled="isLinking"
            secondary-action-text="Cancel"
            @select="selectSearchResult"
            @clear="clearSelectedEntity"
          />
          <Button
            type="primary"
            @click="linkCaseToEntity"
            :disabled="isLinking || !selectedEntityExternalId"
            class="stk-mt-2"
          >
            Link To Entity
          </Button>
          <p v-if="linkError" class="form-error">{{ linkError }}</p>
          <p v-if="linkSuccess" class="success-message">{{ linkSuccess }}</p>
        </div>
        <div v-if="isCaseLinked" class="case-linked-entity">
          <span>Linked entity: {{ selectedCase.entity.name || selectedCase.entity.external_id }}</span>
          <router-link
            :to="{
              name: 'entity-cases',
              params: { entityExternalId: selectedCase.entity.external_id },
              query: { caseId }
            }"
          >
            Open in Entity Cases
          </router-link>
        </div>
        <CaseDetailPanel
          :selected-case="selectedCase"
          :comments="comments"
          :attachments="attachments"
          :attachments-zip-download-url="attachmentsZipDownloadUrl"
          :is-loading-detail="isLoadingDetail"
          :is-submitting-comment="isSubmittingComment"
          :is-submitting-attachment="isSubmittingAttachment"
          :attachment-error="attachmentError"
          :comment-error="commentError"
          :new-comment="newComment"
          @refresh-detail="fetchCaseDetail"
          @update:new-comment="updateNewComment"
          @submit-comment="submitComment"
          @submit-attachment="submitAttachment"
        />
      </div>
    </Box>
  </Stack>
</template>

<script>
import {
  Box,
  BoxHead,
  Button,
  Stack,
  Title,
} from '@stakeui';
import CaseDetailPanel from '@frontend/ui/features/example/components/CaseDetailPanel.vue';
import EntitySearchWithActions from '@frontend/ui/features/example/components/EntitySearchWithActions.vue';
import {
  getJsonHeaders,
  postCaseComment,
  uploadCaseAttachments,
} from '@frontend/ui/features/example/utils/caseActivityApi';
import * as Sentry from '@sentry/vue';

export default {
  name: 'CaseDetail',
  components: {
    Box,
    BoxHead,
    Button,
    CaseDetailPanel,
    EntitySearchWithActions,
    Stack,
    Title,
  },
  props: {
    caseId: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      selectedCase: null,
      comments: [],
      attachments: [],
      newComment: '',
      isLoadingDetail: false,
      isSubmittingComment: false,
      isSubmittingAttachment: false,
      loadError: '',
      attachmentError: '',
      commentError: '',
      selectedEntityExternalId: '',
      selectedEntityOption: null,
      isLinking: false,
      linkError: '',
      linkSuccess: '',
    };
  },
  created() {
    if (this.$route?.query?.attachmentUploadFailed === '1') {
      this.reportAttachmentUploadFailure({
        source: 'route_query_attachment_upload_failed',
      });
      this.attachmentError = 'Attachment failed to upload, please try again.';
    }
    this.fetchCaseDetail();
  },
  watch: {
    caseId() {
      this.fetchCaseDetail();
    },
  },
  computed: {
    isCaseLinked() {
      return Boolean(this.selectedCase?.entity?.external_id);
    },
    attachmentsZipDownloadUrl() {
      if (!this.caseId) {
        return '';
      }
      return `/api/v1/cases/${this.caseId}/attachments/download-zip/`;
    },
  },
  methods: {
    reportAttachmentUploadFailure({ source, error = null, files = [] } = {}) {
      const normalizedFiles = Array.isArray(files) ? files : [];
      const fileDetails = normalizedFiles.map(file => ({
        name: file?.name || '',
        size: file?.size || 0,
        type: file?.type || '',
      }));
      const sentryContext = {
        source: source || 'unknown',
        caseId: this.caseId || null,
        entityExternalId: this.selectedCase?.entity?.external_id || null,
        routeName: this.$route?.name || null,
        routePath: this.$route?.fullPath || null,
        attachmentUploadFailedFlag: this.$route?.query?.attachmentUploadFailed === '1',
        fileCount: normalizedFiles.length,
        fileDetails,
        uploadContext: error?.uploadContext || null,
        rootCause: error?.cause?.message || error?.message || '',
      };

      if (error) {
        Sentry.captureException(error, {
          tags: {
            feature: 'case_detail',
            operation: 'upload_case_attachment',
            surface: 'attachment_error_banner',
          },
          extra: sentryContext,
        });
        return;
      }

      Sentry.captureMessage('Attachment failed to upload shown to user', {
        level: 'warning',
        tags: {
          feature: 'case_detail',
          operation: 'upload_case_attachment',
          surface: 'attachment_error_banner',
        },
        extra: sentryContext,
      });
    },
    async fetchCaseDetail() {
      this.loadError = '';
      this.isLoadingDetail = true;
      try {
        const response = await fetch(`/api/v1/cases/${this.caseId}/`);
        if (!response.ok) {
          throw new Error('Failed to fetch case detail');
        }
        const data = await response.json();
        this.selectedCase = data.case || null;
        this.comments = data.comments || [];
        this.attachments = data.attachments || [];
        this.commentError = '';
        if (this.selectedCase && this.selectedCase.entity && this.selectedCase.entity.external_id) {
          this.selectedEntityExternalId = this.selectedCase.entity.external_id;
          this.selectedEntityOption = this.selectedCase.entity;
        }
      } catch (error) {
        console.error(error);
        Sentry.captureException(error);
        this.loadError = 'Failed to load case detail';
      } finally {
        this.isLoadingDetail = false;
      }
    },
    clearSelectedEntity() {
      this.selectedEntityOption = null;
      this.selectedEntityExternalId = '';
    },
    selectSearchResult(option) {
      this.selectedEntityOption = option || null;
      this.selectedEntityExternalId = option?.external_id || '';
    },
    async linkCaseToEntity() {
      if (!this.selectedEntityExternalId) {
        return;
      }
      this.isLinking = true;
      this.linkError = '';
      this.linkSuccess = '';
      try {
        const response = await fetch(`/api/v1/cases/${this.caseId}/link-entity/`, {
          method: 'POST',
          headers: getJsonHeaders(this.$root.$data.csrfToken),
          body: JSON.stringify({
            entity_external_id: this.selectedEntityExternalId,
          }),
        });
        if (!response.ok) {
          const errorData = await response.json();
          this.linkError = errorData?.error || errorData?.entity_external_id || 'Failed to link case';
          Sentry.captureMessage('Failed to link case to entity', {
            level: 'warning',
            extra: {
              caseId: this.caseId,
              entityExternalId: this.selectedEntityExternalId,
              responseStatus: response.status,
              error: this.linkError,
            },
          });
          return;
        }
        const data = await response.json();
        this.selectedCase = data.case || this.selectedCase;
        this.linkSuccess = 'Case linked successfully.';
        const linkedEntityExternalId = this.selectedCase?.entity?.external_id || this.selectedEntityExternalId;
        if (linkedEntityExternalId) {
          this.$router.push({
            name: 'entity-cases',
            params: {
              entityExternalId: linkedEntityExternalId,
            },
            query: {
              caseId: this.caseId,
            },
          });
        }
      } catch (error) {
        console.error(error);
        Sentry.captureException(error);
        this.linkError = 'Failed to link case';
      } finally {
        this.isLinking = false;
      }
    },
    updateNewComment(value) {
      this.newComment = value;
      this.commentError = '';
    },
    async submitComment() {
      if (!this.caseId || !this.newComment.trim()) {
        return;
      }
      this.commentError = '';
      this.isSubmittingComment = true;
      try {
        const data = await postCaseComment({
          url: `/api/v1/cases/${this.caseId}/comments/`,
          body: this.newComment,
          csrfToken: this.$root.$data.csrfToken,
        });
        this.comments = data.comments || [];
        this.newComment = '';
        this.commentError = '';
      } catch (error) {
        console.error(error);
        Sentry.captureException(error);
        this.commentError = 'Comment failed to post, please try again.';
      } finally {
        this.isSubmittingComment = false;
      }
    },
    async submitAttachment(filesToUpload = []) {
      const files = Array.isArray(filesToUpload) ? filesToUpload : [];
      if (!this.caseId || !files.length) {
        return;
      }
      this.attachmentError = '';
      this.isSubmittingAttachment = true;
      try {
        const data = await uploadCaseAttachments({
          url: `/api/v1/cases/${this.caseId}/attachments/`,
          files,
          csrfToken: this.$root.$data.csrfToken,
        });
        this.attachments = data?.attachments || this.attachments;
      } catch (error) {
        console.error(error);
        this.reportAttachmentUploadFailure({
          source: 'submit_attachment_error',
          error,
          files,
        });
        this.attachmentError = 'Attachment failed to upload, please try again.';
      } finally {
        this.isSubmittingAttachment = false;
      }
    },
  },
};
</script>

<style scoped>
.cases-toolbar {
  display: flex;
  gap: var(--stk-size-4);
  margin-bottom: var(--stk-size-4);
}

.case-link-panel {
  margin-bottom: var(--stk-size-5);
  border: 1px solid var(--stk-color-border);
  border-radius: var(--stk-radius-2);
}

.case-linked-entity {
  display: flex;
  gap: var(--stk-size-3);
  align-items: center;
  margin-bottom: var(--stk-size-4);
}

.empty-state {
  color: var(--stk-color-text-muted);
}

.form-error {
  color: var(--stk-color-danger);
}

.success-message {
  color: var(--stk-color-success);
}
</style>
